import { Dashboard, Logout, } from "@mui/icons-material";
import { NavLink } from "react-router-dom";
import { Badge } from "@mui/material";
import { useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { privateAxios } from "../../../config/apiConfig";
import menuConfig from "../../../config/MenuConfig";

const Sidebar = ({ collapsed, onLogout, user }) => {
  const [allowedUrls, setAllowedUrls] = useState([]);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const token = localStorage.getItem("jwt");
        if (!token) {
          setAllowedUrls([]);
          return;
        }

        const headers = { Authorization: `Bearer ${token}` };
        const response = await privateAxios.get("admin/group-maps/user", { headers });

        const groupMap = response.data;

        // 🔹 Extract only category, page, permission
        const permissionsByPage = (groupMap.groupPermissions || [])
          .map(gp => ({
            category: gp.feature?.category,
            page: gp.feature?.pageName,
            permission: gp.permission?.permissionName
          }))
          .filter(p => p.category && p.page && p.permission); // null/empty हटाने के लिए

        // 🔹 LocalStorage me save karo
        localStorage.setItem("permissions", JSON.stringify(permissionsByPage));

        // 🔹 Extract allowed URLs where permission is READ or WRITE
        const urls = (groupMap.groupPermissions || [])
          .filter(gp => ["READ", "WRITE"].includes(gp.permission?.permissionName))
          .map(gp => gp.feature?.url)
          .filter(Boolean); // remove null/undefined

        setAllowedUrls(urls);
      } catch (error) {
        setAllowedUrls([]); // fallback: nothing
      }
    };

    fetchPermissions();
  }, []);

  // Filter menu items by allowed URL list
  // Filter and transform menu items
  const allowedMenuItems = useMemo(() => {
    return menuConfig
      .filter(item => item.category === "Employee" || allowedUrls.includes(item.path))
      .map(item => {
        if (item.path.startsWith("/admin")) {
          return {
            ...item,
            path: `/employee${item.path}`, // prefix with /employee
          };
        }
        if (item.path.startsWith("/vendor")) {
          return {
            ...item,
            path: `/employee${item.path}`, // prefix with /employee
          };
        }
        return item;
      });
  }, [allowedUrls]);


  const pendingOrdersCount = useSelector((state) => {
    const employeeOrders = state.employeeOrders?.orders?.response || [];
    return employeeOrders.filter(order => order.orderStatus !== "DELIVERED" && order.orderStatus !== "COMPLETED").length;
  });

  return <aside className={`bg-white shadow-md h-full flex flex-col transition-all duration-300 ${collapsed ? "w-16" : "w-64"}`}>
    {/* Top Header Section */}
    <div className="p-4 text-xl font-bold flex items-center justify-center justify-content-center gap-2 border-b" style={{ fontFamily: "Greycliff CF" }}>
      <Dashboard className="text-[#449AEF]" />
      {!collapsed && "EMPLOYEE"}
    </div>

    {/* Scrollable Menu Section */}
    <div className="flex-1 overflow-y-auto">
      <nav className="flex flex-col p-2">
        {Object.entries(
          allowedMenuItems.reduce((acc, item) => {
            if (!acc[item.category]) acc[item.category] = [];
            acc[item.category].push(item);
            return acc;
          }, {})
        ).map(([category, items]) => (
          <div key={category} className="mb-2">
            {!collapsed && (
              <div className="text-xs font-semibold text-gray-500 uppercase px-2 py-1">
                {category}
              </div>
            )}
            {items.map(({ name, path, icon: Icon }) => {
              const showBadge = name === "Orders" && category === "Employee";
              return (
                <NavLink
                  key={path}
                  to={path}
                  end
                  className={({ isActive }) =>
                    `flex items-center p-1 mt-1 mb-1 rounded hover:bg-[#449AEF] hover:text-white no-underline ${isActive ? "bg-[#449AEF] font-bold text-white" : ""
                    }`
                  }
                  style={{ textDecoration: "none", fontFamily: "Greycliff CF" }}
                >
                  {showBadge && pendingOrdersCount > 0 ? (
                    <Badge badgeContent={pendingOrdersCount} color="error" overlap="rectangular">
                      <Icon className="h-5 w-5" />
                    </Badge>
                  ) : (
                    <Icon className="h-5 w-5" />
                  )}

                  {!collapsed && <>{name}</>}
                </NavLink>
              );
            })}
          </div>
        ))}


      </nav>
    </div>

    {/* Bottom User Section */}
    <div className="p-1 border-t mb-1">
      {!collapsed ? (
        <div className="flex items-center justify-between w-full">
          {/* Left side - Avatar + Name + Email */}
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-[#449AEF] text-white flex items-center justify-center text-md font-bold uppercase">
              {user?.name?.charAt(0) || "E"}
            </div>
            <div>
              <div className="font-medium truncate max-w-[120px]">{user?.name || "Employee"}</div>
              <div className="text-gray-500 text-sm truncate max-w-[160px]">
                {user?.email || "Employee@gmail.com"}
              </div>
            </div>
          </div>

          {/* Right side - Logout Button */}
          <button
            onClick={onLogout}
            className="p-1 rounded-full hover:bg-gray-100 transition-colors"
            title="Logout"
          >
            <Logout className="text-gray-700 hover:text-[#449AEF]" />
          </button>
        </div>
      ) : (
        <div className="flex justify-center">
          <button
            onClick={onLogout}
            className="p-1 rounded-full hover:bg-gray-100 transition-colors"
            title="Logout"
          >
            <Logout className="text-gray-700 hover:text-[#449AEF]" />
          </button>
        </div>
      )}
    </div>
  </aside>;
};

export default Sidebar;
